import React, { useState, useEffect } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { ImCross } from "react-icons/im";
import { TiTick } from "react-icons/ti";
import { MdChildCare } from "react-icons/md";
import { IoPerson } from "react-icons/io5";
import { getPrice } from "../../services/priceUtils";
import { useCurrency } from '../../context/CurrencyContext';
import { Modal, Button } from "react-bootstrap";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const RateCard = ({ rate, thumbnail, roomName, checkIn, checkOut }) => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem('user_data'));
    const handleBookSubmit = async (offerKey) => {
        try {
            // Kullanıcı kontrolü
            if (!userData?.token) {
                Swal.fire({
                    title: "Lütfen önce giriş yapın!",
                    icon: "error",
                    draggable: true,
                });
                return;
            }

            // offerKey kontrolü
            if (!offerKey) {
                Swal.fire({
                    title: "Hata!",
                    text: "Rezervasyon anahtarı bulunamadı",
                    icon: "error",
                    draggable: true,
                });
                return;
            }

            // Checkout sayfasına yönlendirme
            navigate('/checkout', {
                state: {
                    offerKey: String(offerKey),
                    thumbnail: thumbnail
                }
            });
            window.scrollTo(0, 0);
        } catch (error) {
            console.error("Error:", error);
            Swal.fire({
                title: "Rezervasyon Hatası",
                text: error.message || "Rezervasyon sırasında bir hata oluştu",
                icon: "error",
                draggable: true,
            });
        }
    };
    const [prices, setPrices] = useState({
        total: "0.00",
        cancellation: "0.00"
    });
    const [cancellationPrices, setCancellationPrices] = useState({});
    const [selectedRate, setSelectedRate] = useState(null);
    const { selectedCurrency } = useCurrency();

    const formatCancellationDate = (dateString) => {
        const date = new Date(dateString);
        const hours = date.getHours();
        const minutes = date.getMinutes();
        
        // Tarih formatı için temel ayarlar
        const dateOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        };

        // Eğer saat 03:00 ise 23:59 göster, değilse ve 00:00 değilse saati göster
        if (hours === 3 && minutes === 0) {
            return `${new Intl.DateTimeFormat('en-US', dateOptions).format(date)} at 23:59`;
        } else if (hours === 0 && minutes === 0) {
            return new Intl.DateTimeFormat('en-US', dateOptions).format(date);
        } else {
            return `${new Intl.DateTimeFormat('en-US', dateOptions).format(date)} at ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
    };
    // Gece sayısını hesaplayan fonksiyon
    const calculateNights = () => {
        // Önce rate içindeki nights değerini kontrol et
        if (rate?.nights) {
            return rate.nights;
        }

        // Eğer nights değeri yoksa, mevcut hesaplama yöntemini kullan
        if (rate?.rateKey) {
            const dates = rate.rateKey.split('|');
            if (dates.length >= 2) {
                const checkIn = new Date(dates[0].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
                const checkOut = new Date(dates[1].replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
                const diffTime = Math.abs(checkOut - checkIn);
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            }
        }
        return 1; // Varsayılan değer
    };
    const [agencyId, setAgencyId] = useState(null);
    const nights = calculateNights();
    const perNightPrice = prices.total && nights ? (Number(prices.total) / nights).toFixed(2) : "0.00";

    useEffect(() => {
        const calculatePrices = async () => {
            const userData = JSON.parse(localStorage.getItem('user_data'));
            if (userData?.token && rate?.net) {
                try {
                    const [totalPrice, cancellationPrice] = await Promise.all([
                        getPrice(rate.net, userData.token, selectedCurrency, rate.taxes, rate?.country?.code, rate.currency),
                        rate?.cancellationPolicies?.[0]?.amount ?
                            getPrice(rate.cancellationPolicies[0].amount, userData.token, selectedCurrency, null, null, rate.currency) :
                            "0.00"
                    ]);

                    // İptal politikası ücretlerini hesapla
                    const newCancellationPrices = {};
                    if (rate?.cancellationPolicies?.length > 0) {
                        for (const policy of rate.cancellationPolicies) {
                            if (policy.amount) {
                                if (rate.currency === 'EUR') {
                                    newCancellationPrices[policy.from] = policy.amount;
                                } else {
                                    const convertedPrice = await getPrice(
                                        policy.amount,
                                        userData.token,
                                        selectedCurrency,
                                        null,
                                        null,
                                        rate.currency
                                    );
                                    newCancellationPrices[policy.from] = convertedPrice;
                                }
                            }
                        }
                    }

                    setPrices({
                        total: totalPrice || "0.00",
                        cancellation: cancellationPrice || "0.00"
                    });
                    setCancellationPrices(newCancellationPrices);
                } catch (error) {
                    console.error("Error calculating prices:", error);
                    setPrices({
                        total: "0.00",
                        cancellation: "0.00"
                    });
                    setCancellationPrices({});
                }
            }
        };
        calculatePrices();
        const storedAgencyId = localStorage.getItem('agency_id');
        setAgencyId(storedAgencyId ? Number(storedAgencyId) : null);
    }, [rate, selectedCurrency]);

    return (
        <div 
            className="d-flex align-items-center border-bottom pt-3"
            style={{ 
                width: "100%", 
                padding: "10px",
                gap: "20px",
                minHeight: "80px",
                transition: "all 0.3s ease-in-out",
                opacity: 0,
                animation: "fadeIn 0.3s ease-in-out forwards"
            }}
        >

            {/* Oda Adı Sütunu */}
            <div style={{ 
                width: "25%",
                minWidth: "200px",
                padding: "0 10px"
            }}>
                <h6 className="fs-14 mb-2" style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                }}>
                    <span className="text-capitalize">
                        {agencyId === 1 && rate?.provider + " - "}
                        {roomName?.toLowerCase() || 'Room'}
                    </span> ({rate?.boardCode?.toUpperCase() || 'N/A'})
                </h6>
                {rate?.allotment < 5 && (
                    <div className="d-flex">
                        <p className="badge badge-info-100 fs-14 fw-medium rounded-pill mb-0 d-none">
                            Last {rate.allotment} room left
                        </p>
                    </div>
                )}
            </div>

            {/* İptal Politikası Sütunu */}
            <div style={{ 
                width: "45%",
                minWidth: "300px",
                padding: "0 10px"
            }}>
                {rate?.rateClass === "NOR" ? (
                    rate?.cancellationPolicies?.length > 0 ? (
                        <button className="btn btn-sm border w-100 h-100" onClick={() => setSelectedRate(rate)}>
                            <div className="d-flex align-items-center">
                                <TiTick style={{ color: "green", fontSize: "20px", flexShrink: 0, marginRight: "8px" }} />
                                <p className="mb-0 text-black" style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap"
                                }}>
                                    {rate?.cancellationPolicies?.length > 0 ? 
                                        `Free Cancellation until ${formatCancellationDate(rate.cancellationPolicies[0].from)}` : 
                                        'Non-Refundable'
                                    }
                                </p>
                            </div>
                        </button>
                    ) : (
                        <button className="btn btn-sm border w-100 h-100">
                            <div className="d-flex align-items-center">
                                <ImCross style={{ color: "red", flexShrink: 0, marginRight: "8px" }} />
                                <p className="mb-0 text-black">Non-Refundable</p>
                            </div>
                        </button>
                    )
                ) : (
                    <button className="btn btn-sm border w-100 h-100">
                        <div className="d-flex align-items-center">
                            <ImCross style={{ color: "red", flexShrink: 0, marginRight: "8px" }} />
                            <p className="mb-0 text-black fs-14">Non-Refundable</p>
                        </div>
                    </button>
                )}
            </div>

            {/* Fiyat ve Rezervasyon Sütunu */}
            <div style={{ 
                width: "30%",
                minWidth: "250px",
                padding: "0 10px",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: "15px"
            }}>
                <div className="text-end">
                    <h5 className="text-primary mb-1 fs-14" style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}>
                        {prices.total} {selectedCurrency}
                        <span className="fs-14 fw-normal text-default"> / {nights} {nights === 1 ? 'Night' : 'Nights'}</span>
                    </h5>
                    <small className="text-muted fs-12" style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                    }}>
                        {perNightPrice} {selectedCurrency} per night
                    </small>
                </div>
                <button
                    onClick={() => handleBookSubmit(rate?.rateKey || rate?.key || rate?.offerKey)}
                    type="button"
                    className={`btn ${rate?.isOnRequest ? 'btn-request' : 'btn-primary'}`}
                    style={{ 
                        whiteSpace: "nowrap",
                        ...(rate?.isOnRequest && {
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            border: '1px solid #90caf9',
                            boxShadow: '0 2px 4px rgba(25, 118, 210, 0.1)'
                        })
                    }}
                    disabled={!prices.total || prices.total === "0.00" || !(rate?.rateKey || rate?.key || rate?.offerKey)}
                >
                    {rate?.isOnRequest ? 'Request' : 'Book Now'}
                </button>
            </div>

            <Modal
                show={selectedRate !== null}
                onHide={() => setSelectedRate(null)}
                centered
                backdropClassName="custom-backdrop"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Cancellation Policy</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedRate?.cancellationPolicies
                        ?.filter(policy => Number(policy.amount) > 0)
                        ?.map((policy, index) => {
                        const policyDate = new Date(policy.from);
                        const checkInDate = new Date(checkIn);
                        
                        return (
                            <div key={index} className="cancellation-policy-container">
                                {/* Free Cancellation Badge - Her zaman göster */}
                                <div className="modern-badge" 
                                    style={{
                                        background: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)',
                                        boxShadow: '0 2px 8px rgba(76, 175, 80, 0.15)'
                                    }}>
                                    <div className="badge-icon" style={{ background: '#4CAF50' }}>
                                        <i className="isax isax-tick-circle"></i>
                                    </div>
                                    <div className="badge-content">
                                        <h6 className="badge-title" style={{ color: '#2E7D32' }}>
                                            Free Cancellation Period
                                        </h6>
                                        <p className="badge-text" style={{ color: '#1B5E20' }}>
                                            Cancel without any charges until {formatCancellationDate(policy.from)}
                                        </p>
                                    </div>
                                </div>

                                {/* Partial Charge Badge */}
                                <div className="modern-badge"
                                    style={{
                                        background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                                        boxShadow: '0 2px 8px rgba(255, 152, 0, 0.15)'
                                    }}>
                                    <div className="badge-icon" style={{ background: '#FF9800' }}>
                                        <i className="isax isax-info-circle"></i>
                                    </div>
                                    <div className="badge-content">
                                        <h6 className="badge-title" style={{ color: '#E65100' }}>
                                            Cancellation Fee Period
                                        </h6>
                                        <p className="badge-text" style={{ color: '#F57C00' }}>
                                            From {formatCancellationDate(policy.from)} until {formatCancellationDate(checkIn)}<br/>
                                            <span className="fw-semibold">
                                                Fee: {cancellationPrices[policy.from] || "0.00"} {selectedCurrency}
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                {/* Non-Refundable Badge */}
                                <div className="modern-badge"
                                    style={{
                                        background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                                        boxShadow: '0 2px 8px rgba(244, 67, 54, 0.15)'
                                    }}>
                                    <div className="badge-icon" style={{ background: '#F44336' }}>
                                        <i className="isax isax-danger"></i>
                                    </div>
                                    <div className="badge-content">
                                        <h6 className="badge-title" style={{ color: '#D32F2F' }}>
                                            Non-Refundable Period
                                        </h6>
                                        <p className="badge-text" style={{ color: '#B71C1C' }}>
                                            After {formatCancellationDate(checkIn)}<br/>
                                            <span className="fw-semibold">
                                                Full amount will be charged
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    
                    {!selectedRate?.cancellationPolicies?.some(policy => Number(policy.amount) > 0) && (
                        <div className="modern-badge"
                            style={{
                                background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
                                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)'
                            }}>
                            <div className="badge-icon" style={{ background: '#2196F3' }}>
                                <i className="isax isax-info-circle"></i>
                            </div>
                            <div className="badge-content">
                                <h6 className="badge-title" style={{ color: '#1976D2' }}>
                                    No Cancellation Policy
                                </h6>
                                <p className="badge-text" style={{ color: '#0D47A1' }}>
                                    Please contact customer service for details
                                </p>
                            </div>
                        </div>
                    )}

                    <style jsx>{`
                        .cancellation-policy-container {
                            display: flex;
                            flex-direction: column;
                            gap: 1rem;
                            margin-bottom: 1rem;
                        }
                        
                        .modern-badge {
                            display: flex;
                            align-items: flex-start;
                            padding: 1.25rem;
                            border-radius: 12px;
                            transition: all 0.3s ease;
                        }
                        
                        .modern-badge:hover {
                            transform: translateY(-2px);
                        }
                        
                        .badge-icon {
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            width: 36px;
                            height: 36px;
                            border-radius: 10px;
                            margin-right: 1rem;
                            flex-shrink: 0;
                        }
                        
                        .badge-icon i {
                            color: white;
                            font-size: 20px;
                        }
                        
                        .badge-content {
                            flex: 1;
                        }
                        
                        .badge-title {
                            font-size: 1rem;
                            font-weight: 600;
                            margin-bottom: 0.5rem;
                        }
                        
                        .badge-text {
                            font-size: 0.875rem;
                            margin-bottom: 0;
                            line-height: 1.5;
                        }
                    `}</style>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setSelectedRate(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default RateCard;

// Global styles
const styles = `
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.room-contents {
    transition: all 0.3s ease-in-out;
}

.show-more-button {
    transition: all 0.3s ease;
}

.show-more-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
`;

// Add styles to document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);
